import React, { useState, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom/client';
import { CssBaseline, ThemeProvider, useMediaQuery } from '@mui/material';
import Cookies from 'js-cookie';
import { getAppTheme } from './theme';
import { getTranslation } from './i18n';
import { syncStatusBar } from './utils/nativeBridge';
import App from './App';

const RootComponent = () => {
  const prefersDarkMode = useMediaQuery('(prefers-color-scheme: dark)');
  const savedTheme = Cookies.get('theme');
  const savedLang = Cookies.get('lang') || 'fa';

  const [isDarkMode, setIsDarkMode] = useState(savedTheme ? savedTheme === 'dark' : prefersDarkMode);
  const [language, setLanguage] = useState(savedLang);

  useEffect(() => {
    setIsDarkMode(savedTheme ? savedTheme === 'dark' : prefersDarkMode);
  }, [prefersDarkMode, savedTheme]);

  useEffect(() => {
    const metaThemeColors = document.querySelectorAll('meta[name="theme-color"]');
    metaThemeColors.forEach((meta) => meta.setAttribute('content', isDarkMode ? '#0B0F17' : '#FFFFFF'));

    const appleStatus = document.querySelector('meta[name="apple-mobile-web-app-status-bar-style"]');
    if (appleStatus) {
      appleStatus.setAttribute('content', isDarkMode ? 'black-translucent' : 'default');
    }

    if (document.documentElement) {
      document.documentElement.style.backgroundColor = isDarkMode ? '#0B0F17' : '#FFFFFF';
    }
    if (document.body) {
      document.body.style.backgroundColor = isDarkMode ? '#0B0F17' : '#F8FAFC';
    }

    syncStatusBar(isDarkMode);
  }, [isDarkMode]);

  useEffect(() => {
    document.documentElement.dir = language === 'fa' ? 'rtl' : 'ltr';
    document.documentElement.lang = language;
  }, [language]);

  const toggleTheme = () => {
    const newTheme = !isDarkMode ? 'dark' : 'light';
    setIsDarkMode(!isDarkMode);
    Cookies.set('theme', newTheme, { expires: 365 });
  };

  const toggleLanguage = () => {
    const newLang = language === 'fa' ? 'en' : 'fa';
    setLanguage(newLang);
    Cookies.set('lang', newLang, { expires: 365 });
  };

  const t = useCallback((key, params) => getTranslation(language, key, params), [language]);

  const theme = getAppTheme(isDarkMode ? 'dark' : 'light', language === 'fa' ? 'rtl' : 'ltr');

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <App
        toggleTheme={toggleTheme}
        language={language}
        toggleLanguage={toggleLanguage}
        t={t}
      />
    </ThemeProvider>
  );
};

const root = ReactDOM.createRoot(document.getElementById('root'));

root.render(<RootComponent />);

// Helper: check if element is an editable input/textarea
const isEditable = (el) => {
  if (!el) return false;
  return el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.isContentEditable;
};

// Suppress long-press and right-click context menu across the app
window.addEventListener('contextmenu', (e) => {
  if (isEditable(e.target)) return;
  e.preventDefault();
}, { capture: true, passive: false });

// Suppress long-press text selection start
window.addEventListener('selectstart', (e) => {
  if (isEditable(e.target)) return;
  e.preventDefault();
}, { capture: true, passive: false });

// Clear accidental selections outside editable fields
document.addEventListener('selectionchange', () => {
  if (isEditable(document.activeElement)) return;
  const selection = window.getSelection();
  if (selection && selection.rangeCount > 0) {
    selection.removeAllRanges();
  }
});

window.addEventListener('copy', (e) => {
  if (isEditable(document.activeElement)) return;
  e.preventDefault();
}, { capture: true });

window.addEventListener('cut', (e) => {
  if (isEditable(document.activeElement)) return;
  e.preventDefault();
}, { capture: true });

window.addEventListener('dragstart', (e) => {
  e.preventDefault();
}, { capture: true });

// Register PWA Service Worker for offline performance
if ('serviceWorker' in navigator && process.env.NODE_ENV === 'production') {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('./sw.js').catch((err) => {
      console.error('ServiceWorker registration failed: ', err);
    });
  });
}
